import { Button, Tab, Tabs, Typography } from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import { Skeleton } from '@material-ui/lab';
import classNames from 'classnames';
import React, { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { Link, useParams } from 'react-router-dom';

import { usePostHog } from 'hooks/usePostHog';
import globalMessages from 'shared/global/messages';

import { AI_AGENT_SETUP } from '../constants';
import { messages } from '../messages';
import { useStyles } from './styles';
import { TurnRow } from './TurnRow';
import { useConversationTurns } from './useConversationTurns';

export const ConversationsPage = ({ accountSlug }: { accountSlug: string }) => {
  const { agentId } = useParams<{ agentId: string }>();
  const intl = useIntl();
  const classes = useStyles();
  const posthog = usePostHog();
  const viewed = useRef(false);
  const {
    status,
    agent,
    turns,
    nextCursor,
    loadingMore,
    loadMoreError,
    retry,
    loadMore,
  } = useConversationTurns(accountSlug, agentId);

  useEffect(() => {
    if (viewed.current) return;
    viewed.current = true;
    posthog.capture('candidate_ai_agent:conversations_view', {
      agent_id: agentId,
    });
  }, [posthog, agentId]);

  const errorBar = (onRetry: () => void) => (
    <div className={classes.error} role="alert">
      <ErrorOutlineIcon />
      <Typography variant="body2" className={classes.primary}>
        {intl.formatMessage(messages.loadError)}
      </Typography>
      <Button className={classes.retry} onClick={onRetry}>
        {intl.formatMessage(globalMessages.tryAgain)}
      </Button>
    </div>
  );

  return (
    <div className={classes.page}>
      <Button
        component={Link}
        to={`/${accountSlug}/fountain_ai/${AI_AGENT_SETUP}`}
        className={classes.back}
        startIcon={<ArrowBackIcon />}
      >
        {intl.formatMessage(messages.conversationsBack)}
      </Button>
      <div className={classes.header}>
        <Typography variant="h2">{agent?.name}</Typography>
        <Typography variant="body2" className={classes.muted}>
          {agent?.brand}
        </Typography>
      </div>
      <Tabs
        value="conversations"
        className={classes.tabs}
        aria-label={intl.formatMessage(messages.conversationsTab)}
      >
        <Tab
          value="conversations"
          className={classes.tab}
          id="conversations-tab"
          aria-controls="conversations-content"
          label={intl.formatMessage(messages.conversationsTab)}
        />
      </Tabs>
      <div data-testid="conversations-filter-bar" className={classes.filters} />
      <div
        id="conversations-content"
        role="tabpanel"
        aria-labelledby="conversations-tab"
      >
        {status === 'error' || status === 'too_many_openings' ? (
          errorBar(retry)
        ) : (
          <div
            className={classes.card}
            data-testid="conversations-list"
            aria-busy={status === 'loading' || loadingMore}
          >
            <div
              className={classNames(classes.grid, classes.listHeader)}
              aria-hidden="true"
            >
              {[
                messages.columnApplicant,
                messages.columnQuestion,
                messages.columnAnswer,
                messages.columnDate,
              ].map(message => (
                <Typography variant="body2" key={message.id}>
                  {intl.formatMessage(message)}
                </Typography>
              ))}
              <div />
            </div>
            {status === 'loading' &&
              Array.from({ length: 6 }, (_, index) => (
                <div
                  className={classNames(classes.grid, classes.loadingRow)}
                  key={index}
                  aria-hidden="true"
                >
                  {[
                    [120, 88],
                    ['100%', '60%'],
                    ['100%', '100%', '40%'],
                    [96],
                    [],
                  ].map((widths, cell) => (
                    <div key={cell}>
                      {widths.map((width, line) => (
                        <Skeleton
                          key={line}
                          variant="rect"
                          className={classes.skeleton}
                          width={width}
                        />
                      ))}
                    </div>
                  ))}
                </div>
              ))}
            {status === 'ready' && !turns.length && (
              <div className={classes.empty}>
                <Typography variant="h3">
                  {intl.formatMessage(messages.emptyNoConversationsTitle)}
                </Typography>
                <Typography variant="body2" className={classes.secondary}>
                  {intl.formatMessage(messages.emptyNoConversationsBody)}
                </Typography>
              </div>
            )}
            {turns.map(turn => (
              <TurnRow key={turn.id} turn={turn} />
            ))}
          </div>
        )}
        {status === 'ready' && turns.length > 0 && (
          <div className={classes.more}>
            {loadMoreError && errorBar(loadMore)}
            {nextCursor ? (
              <Button
                variant="outlined"
                className={classes.moreButton}
                disabled={loadingMore}
                onClick={loadMore}
              >
                {intl.formatMessage(messages.loadMore)}
              </Button>
            ) : (
              <Typography variant="body2" className={classes.muted}>
                {intl.formatMessage(messages.endOfList)}
              </Typography>
            )}
          </div>
        )}
      </div>
      <div data-testid="conversations-panel" />
    </div>
  );
};
